import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export const ExploreJewellery = ({ exploreItems, products }) => {
  const getExploreImage = (item) => {
    const itemProducts = products.filter(p => 
      p.category?.toLowerCase() === item.id.toLowerCase()
    );
    if (itemProducts.length > 0 && itemProducts[0].images?.[0]) {
      return itemProducts[0].images[0];
    }
    return item.image;
  };

  const focalStyle = (item) => item.focal_x != null ? { objectPosition: `${item.focal_x}% ${item.focal_y ?? 50}%` } : undefined;
  
  const leftItem = exploreItems[0];
  const middleTopItem = exploreItems[1];
  const middleBottomItem = exploreItems[2];
  const rightItem = exploreItems[3];
  
  return (
    <section className="py-12 md:py-16 bg-[#FEFAF3]/80" data-testid="explore-jewellery">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-[#1a3a5c] mb-2">EXPLORE JEWELLERY</h2>
            <p className="text-gray-600 text-sm md:text-base">Discover exquisite pieces for every style. Start shopping!</p>
          </div>
          <Link to="/products" className="hidden md:flex items-center gap-1 text-[#C9A967] hover:text-[#b8985a] font-medium transition-colors">
            Discover More <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5" style={{ minHeight: '600px' }}>
          {/* Left Column - Tall */}
          {leftItem && (
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="row-span-2">
              <Link to={leftItem.link} className="group block relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 h-full" data-testid={`explore-${leftItem.id}`}>
                <div className="h-full min-h-[400px] md:min-h-[600px]">
                  <img src={getExploreImage(leftItem)} alt={leftItem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={focalStyle(leftItem)} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <h3 className="font-serif text-lg md:text-xl text-white font-semibold">{leftItem.name}</h3>
                </div>
              </Link>
            </motion.div>
          )}
          
          {/* Middle Column - Stacked */}
          <div className="flex flex-col gap-4 md:gap-5">
            {middleTopItem && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                <Link to={middleTopItem.link} className="group block relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300" data-testid={`explore-${middleTopItem.id}`}>
                  <div className="aspect-square">
                    <img src={getExploreImage(middleTopItem)} alt={middleTopItem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={focalStyle(middleTopItem)} loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                    <h3 className="font-serif text-base md:text-lg text-white font-semibold">{middleTopItem.name}</h3>
                  </div>
                </Link>
              </motion.div>
            )}
            
            {middleBottomItem && (
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                <Link to={middleBottomItem.link} className="group block relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300" data-testid={`explore-${middleBottomItem.id}`}>
                  <div className="aspect-square">
                    <img src={getExploreImage(middleBottomItem)} alt={middleBottomItem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={focalStyle(middleBottomItem)} loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                    <h3 className="font-serif text-base md:text-lg text-white font-semibold">{middleBottomItem.name}</h3>
                  </div>
                </Link>
              </motion.div>
            )}
          </div>
          
          {/* Right Column - Desktop */}
          {rightItem && (
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="hidden md:block row-span-2">
              <Link to={rightItem.link} className="group block relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 h-full bg-[#1a1a1a]" data-testid={`explore-${rightItem.id}`}>
                <div className="h-full min-h-[600px] flex flex-col">
                  <div className="p-6 text-center">
                    <div className="text-white/80 text-xs tracking-[0.3em] mb-1">— MARISSA —</div>
                    <div className="text-white text-lg font-serif tracking-wider">{rightItem.name}</div>
                  </div>
                  <div className="flex-1 relative">
                    <img src={getExploreImage(rightItem)} alt={rightItem.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" style={focalStyle(rightItem)} loading="lazy" />
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <span className="bg-white/90 text-gray-800 px-4 py-2 text-sm font-medium">{rightItem.name}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}
          
          {/* Right Column - Mobile */}
          {rightItem && (
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="md:hidden">
              <Link to={rightItem.link} className="group block relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300" data-testid={`explore-${rightItem.id}-mobile`}>
                <div className="aspect-square">
                  <img src={getExploreImage(rightItem)} alt={rightItem.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={focalStyle(rightItem)} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                  <h3 className="font-serif text-base text-white font-semibold">{rightItem.name}</h3>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
        
        <div className="md:hidden text-center mt-6">
          <Link to="/products" className="inline-flex items-center gap-1 text-[#C9A967] hover:text-[#b8985a] font-medium">
            Discover More <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
